import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import GlassCard from "@/components/GlassCard";
import { BlurText, SpotlightCard, ShinyText } from "@/components/animations";
import {
    TrendingUp, CheckCircle2, ArrowUpRight, Github,
    GraduationCap, CreditCard, Briefcase, Share2,
    FileText, Layers, ArrowRight, Sparkles, Globe
} from "lucide-react";

const roadmapItems = [
    {
        category: "Academic Signal",
        color: "#3b82f6",
        icon: GraduationCap,
        items: [
            { title: "Upload Academic Transcripts", desc: "Verified transcripts provide the strongest academic evidence. Upload marksheets from your university portal.", impact: "+15-25 pts", priority: "high" },
            { title: "Add Attendance Records", desc: "High attendance (>85%) adds a positive signal to your academic profile.", impact: "+3-5 pts", priority: "medium" },
        ],
    },
    {
        category: "Fee Payment Signal",
        color: "#06b6d4",
        icon: CreditCard,
        items: [
            { title: "Connect Fee Payment History", desc: "Link your college fee payment records to verify payment consistency and timing.", impact: "+12-20 pts", priority: "high" },
            { title: "Set Up Payment Reminders", desc: "On-time payments significantly boost your score. Never miss a deadline.", impact: "+5-10 pts", priority: "medium" },
        ],
    },
    {
        category: "Developer / Professional Signal",
        color: "#8b5cf6",
        icon: Github,
        items: [
            { title: "Connect GitHub Profile", desc: "Link your GitHub account via OAuth. More repos, stars, and recent activity = higher score.", impact: "+15-30 pts", priority: "high" },
            { title: "Build a Portfolio Website", desc: "A live portfolio site with projects demonstrates professional initiative.", impact: "+10-15 pts", priority: "medium" },
            { title: "Complete LinkedIn Profile", desc: "Add headline, experience, skills, and education to your LinkedIn.", impact: "+10-20 pts", priority: "medium" },
            { title: "Upload Your Resume", desc: "A well-structured resume adds professional credibility to your profile.", impact: "+5-10 pts", priority: "low" },
        ],
    },
    {
        category: "Social / Creator Signal",
        color: "#f59e0b",
        icon: Share2,
        items: [
            { title: "Link Social Media Profiles", desc: "Connect YouTube, Twitter, Instagram, or Reddit to expand your social footprint.", impact: "+10-25 pts", priority: "medium" },
            { title: "Grow Your Online Presence", desc: "Active engagement and consistent posting improve your creator signal over time.", impact: "+5-15 pts", priority: "low" },
        ],
    },
    {
        category: "Coverage & Consistency",
        color: "#10b981",
        icon: Layers,
        items: [
            { title: "Connect More Data Sources", desc: "The more signals you connect, the higher your coverage score. Aim for 4+ categories.", impact: "+10-20 pts", priority: "high" },
            { title: "Keep Data Fresh", desc: "Re-verify your data periodically. Stale data reduces your score confidence.", impact: "+5 pts", priority: "low" },
        ],
    },
];

const priorityBadge = {
    high: { label: "High Impact", class: "bg-green-pioneer/15 text-green-pioneer" },
    medium: { label: "Medium", class: "bg-amber-builder/15 text-amber-builder" },
    low: { label: "Quick Win", class: "bg-blue-primary/15 text-blue-primary" },
};

export default function ImprovementRoadmap() {
    return (
        <div className="pt-24 pb-16 px-6 md:px-12 lg:px-24 min-h-dvh" data-testid="improvement-roadmap">
            <div className="max-w-4xl mx-auto">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <p className="text-xs uppercase tracking-[0.3em] text-green-pioneer font-semibold mb-4">Growth Path</p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-text-primary mb-4">
                        Improve Your Score
                    </h1>
                    <p className="text-text-secondary text-base md:text-lg mb-10 leading-relaxed max-w-2xl">
                        Actionable steps to boost your GenZ Credit Score. Each item shows estimated point impact — start with high-impact actions first.
                    </p>
                </motion.div>

                <div className="space-y-10">
                    {roadmapItems.map((category, ci) => (
                        <motion.div key={category.category}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: ci * 0.08 }}>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: category.color + "1a" }}>
                                    <category.icon size={16} style={{ color: category.color }} />
                                </div>
                                <h2 className="font-heading text-lg font-semibold text-text-primary">{category.category}</h2>
                            </div>
                            <div className="space-y-3">
                                {category.items.map((item, i) => {
                                    const badge = priorityBadge[item.priority];
                                    return (
                                        <GlassCard key={item.title} className="p-5" delay={0}>
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                        <h3 className="font-heading text-sm font-medium text-text-primary">{item.title}</h3>
                                                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badge.class}`}>
                                                            {badge.label}
                                                        </span>
                                                    </div>
                                                    <p className="text-text-secondary text-xs leading-relaxed">{item.desc}</p>
                                                </div>
                                                <div className="flex items-center gap-1.5 shrink-0">
                                                    <span className="font-mono text-sm font-bold" style={{ color: category.color }}>
                                                        {item.impact}
                                                    </span>
                                                    <ArrowUpRight size={12} style={{ color: category.color }} />
                                                </div>
                                            </div>
                                        </GlassCard>
                                    );
                                })}
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Bottom CTA */}
                <motion.div className="mt-16 text-center"
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                    <GlassCard className="p-8" hover={false}>
                        <Sparkles size={24} className="text-cyan-signal mx-auto mb-3" />
                        <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">Ready to boost your score?</h3>
                        <p className="text-text-secondary text-sm mb-5">Start by connecting your strongest data sources.</p>
                        <Link to="/genz-credit-scoring/flow"
                            className="bg-blue-primary text-white font-medium px-8 py-3 rounded-xl hover:bg-blue-primary/90 hover:shadow-[0_0_20px_rgba(59,130,246,0.4)] transition-all text-sm inline-flex items-center gap-2"
                            data-testid="improve-start-btn">
                            Start Assessment <ArrowRight size={16} />
                        </Link>
                    </GlassCard>
                </motion.div>
            </div>
        </div>
    );
}
